import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AppColors } from "../../utility/AppColors";
import {
  FlatList,
  Image,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppImages } from "../../utility/AppImages";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import InviteSocialMediaModal from "../../screens/Auth/Event/InviteSocialMediaModal";
import GiftModal from "../../screens/Auth/WebScreens/Modals/GiftModal";
import { useLiveStream } from "../../context/LiveStreamContext";
import { toast } from "react-toastify";
import { LinearGradient } from "expo-linear-gradient";
import { StyleSheet } from "react-native";
import LikeAnimation from "./LikeAnimation";
import StreamActionIcon from "../StreamActionIcons";
import StreamSharePopup from "./StreamSharePopup";

const StreamChat = ({
  styles,
  setInviteSocialMediaModal,
  setIsGiftModal,
  inviteSocialMediaModal,
  giftData,
  isGiftModal,
  socialMediaOptions,
  shareOnPlatform,
  copyToClipboard,
  handleGiftSelect,
  getTokenPopup,
  setGetTokenPopup,
  isMobile = false,
  onOpenMobileGiftSheet,
}) => {
  const deviceSize = useDeviceSize();
  const flatListRef = useRef<any>(null);
  const inputRef = useRef<any>(null);
  const [likeAnimations, setLikeAnimations] = useState<number[]>([]);
  const {
    audienceData,
    sendComment,
    userData,
    currentEventId,
    totalGiftedTokens,
    viewerDetails,
    totalViewers,
    sendLike,
    setAudienceData,
    setOrganizerData,
    alreadyLiked,
  } = useLiveStream();
  const [newMessage, setNewMessage] = useState("");
  const [pendingMessages, setPendingMessages] = useState<any[]>([]);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const audienceChatData = useMemo(() => {
    const chats = audienceData?.chats || [];
    return chats
      .filter((chat: any) => chat.commentType === "Audience")
      .sort((a: any, b: any) => a.timestamp - b.timestamp);
  }, [audienceData?.chats]);

  const audienceCommentIds = useMemo(() => {
    return new Set((audienceChatData || []).map((c: any) => c.commentId));
  }, [audienceChatData]);

  // Drop pending messages once they are echoed back by server
  useEffect(() => {
    if (pendingMessages.length === 0) return;
    setPendingMessages((prev) =>
      prev.filter((m) => !audienceCommentIds.has(m.commentId))
    );
  }, [audienceCommentIds]);

  const streamChatData = useMemo(() => {
    const combined = [...audienceChatData, ...pendingMessages];
    const byId = new Map<string, any>();
    combined.forEach((m) => {
      if (!byId.has(m.commentId)) byId.set(m.commentId, m);
    });
    return Array.from(byId.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [audienceChatData, pendingMessages]);

  const handleSendComment = useCallback(() => {
    if (newMessage.trim() === "") {
      toast.error("Please enter a message");
      return;
    }

    const userId = userData?.userID;
    const eventId = currentEventId;
    if (!userId || !eventId) {
      toast.error("Unable to send message. Try reloading the stream.");
      return;
    }

    const newComment = {
      userId,
      eventId,
      commentData: newMessage,
      commentType: "Audience",
      timestamp: Date.now(),
      profilePicture: userData?.profilePicture || "",
      commentId: `${userId}-${Date.now()}`,
    };
    setPendingMessages((prev) => [...prev, newComment]);

    sendComment(userId, eventId, newMessage, "Audience");

    setNewMessage("");

    // Scroll to latest message after sending
    setTimeout(() => {
      flatListRef.current?.scrollToEnd({ animated: true });
    }, 100);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 150);
  }, [
    currentEventId,
    newMessage,
    sendComment,
    userData?.profilePicture,
    userData?.userID,
  ]);

  const handleLikeClick = useCallback(() => {
    // Add new animation
    const animationId = Date.now();
    setLikeAnimations((prev) => [...prev, animationId]);

    if (!alreadyLiked) {
      setOrganizerData((prev: any) => ({
        ...prev,
        likes: (prev?.likes || 0) + 1,
      }));

      setAudienceData((prev: any) => ({
        ...prev,
        likes: (prev?.likes || 0) + 1,
      }));
    }

    sendLike();
  }, [alreadyLiked, sendLike, setAudienceData, setOrganizerData]);

  const handleAnimationComplete = useCallback((animationId: number) => {
    setLikeAnimations((prev) => prev.filter((id) => id !== animationId));
  }, []);

  // Handle scroll position detection
  const handleScroll = useCallback(
    (event: any) => {
      const { contentOffset, contentSize, layoutMeasurement } =
        event.nativeEvent;
      const paddingToBottom = 20;
      const isBottom =
        layoutMeasurement.height + contentOffset.y >=
        contentSize.height - paddingToBottom;

      setIsAtBottom(isBottom);
      setShowScrollToBottom(!isBottom && streamChatData.length > 0);
    },
    [streamChatData.length]
  );

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    if (!flatListRef.current || streamChatData.length === 0) return;
    try {
      flatListRef.current.scrollToEnd({ animated: true });
      setShowScrollToBottom(false);
    } catch (e) {
      console.error("Error scrolling to bottom:", e);
    }
  }, [streamChatData.length]);

  // Auto-scroll only when user is at bottom
  const scrollTimerRef = useRef<any>(null);
  useEffect(() => {
    if (!flatListRef.current || streamChatData.length === 0 || !isAtBottom)
      return;
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      try {
        flatListRef.current.scrollToIndex({
          animated: true,
          index: streamChatData.length - 1,
        });
      } catch (e) {}
    }, 100);
    return () => {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
    };
  }, [streamChatData.length, isAtBottom]);

  return (
    <View
      style={[
        styles.streamChatView,
        {
          padding: 0,
          marginVertical: 12,
        },
      ]}
    >
      {deviceSize === DEVICE_SIZES.XL ? (
        <View
          style={{
            backgroundColor: "black",
            height: "100%",
            justifyContent: "space-between",
          }}
        >
          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "#374151",
              padding: 6,
              paddingHorizontal: 12,
              height: 60,
            }}
          >
            {renderLikesViewersSection()}
          </LinearGradient>

          <View style={{ height: 6 }} />

          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "#374151",
              padding: 6,
              paddingHorizontal: 12,
              flex: 1,
            }}
          >
            {renderStreamChatContent()}
          </LinearGradient>

          <View style={{ height: 6 }} />

          <View
            style={{
              borderRadius: 12,
              borderWidth: 1,
              borderColor: "#374151",
              backgroundColor: "rgba(0, 0, 0, 0.20)",
              height: "20%",
              overflow: "hidden",
            }}
          >
            {showSharePopup ? (
              <StreamSharePopup onClose={() => setShowSharePopup(false)} />
            ) : (
              renderCompactAudiencePoll()
            )}
          </View>
        </View>
      ) : (
        <View style={isMobile ? { flex: 1 } : { maxHeight: 300 }}>
          <View
            style={{
              height: 39,
              marginBottom: 8,
              borderColor: "white",
              borderWidth: 1,
              borderRadius: 6,
              padding: 4,
            }}
          >
            {renderLikesViewersSection()}
          </View>
          <View style={{ flex: 1 }}>{renderStreamChatContent()}</View>
        </View>
      )}
    </View>
  );

  function renderLikesViewersSection() {
    return (
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          height: "100%",
          alignItems: "center",
        }}
      >
        {/* Likes */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Text style={{ color: "white", fontSize: 12, opacity: 0.7 }}>
            Likes
          </Text>
          <Text style={{ color: "white", fontSize: 16, fontWeight: "600" }}>
            {audienceData?.likes ?? 0}
          </Text>
        </View>

        {/* Divider */}
        <View
          style={{
            width: 1,
            height: 24,
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        />

        {/* Total Viewers */}
        <View
          style={{
            flexDirection: "column",
            alignItems: "center",
            gap: 4,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Text style={{ color: "white", fontSize: 12, opacity: 0.7 }}>
              Total Viewers:
            </Text>
            <Text style={{ color: "white", fontSize: 16, fontWeight: "600" }}>
              {totalViewers ?? 0}
            </Text>
          </View>
          {viewerDetails.length > 0 && (
            <View style={{ flexDirection: "row" }}>
              {(() => {
                const unique = [
                  ...new Map(
                    viewerDetails.map((d: any) => [d.userId, d])
                  ).values(),
                ].slice(0, 7);
                return unique.map((details: any, index: number) => (
                  <View
                    key={details.userId}
                    style={{
                      width: 18,
                      height: 18,
                      backgroundColor: AppColors.white,
                      borderRadius: 9,
                      overflow: "hidden",
                      marginLeft: index > 0 ? -6 : 0,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Image
                      source={
                        details.profilePicture
                          ? { uri: details.profilePicture }
                          : AppImages.Ellipse1
                      }
                      style={{
                        width: 18,
                        height: 18,
                      }}
                      resizeMode="cover"
                    />
                  </View>
                ));
              })()}
            </View>
          )}
        </View>
      </View>
    );
  }

  function renderStreamChatContent() {
    return (
      <>
        <View
          style={{
            flex: 1,
          }}
        >
          {deviceSize == DEVICE_SIZES.XS || deviceSize == DEVICE_SIZES.SM ? (
            ""
          ) : (
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: "#374151",
                paddingBottom: 8,
                marginBottom: 8,
              }}
            >
              <Text
                style={[
                  deviceSize === DEVICE_SIZES.XL
                    ? AppFontStyle.BOLD_ANYBODY_18
                    : AppFontStyle.BOLD_ANYBODY_14,
                  { marginVertical: 10 },
                ]}
              >
                Stream Chat
              </Text>
            </View>
          )}

          <FlatList
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            ref={flatListRef}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            style={{
              flex: 1,
            }}
            contentContainerStyle={{
              paddingVertical: 8,
            }}
            data={streamChatData}
            keyExtractor={(item: any) => item.commentId}
            renderItem={({ item }) => (
              <View
                style={{
                  flexDirection: "row",
                  marginVertical: 8,
                  columnGap: 10,
                }}
              >
                <Image
                  source={
                    item.profilePicture
                      ? { uri: item.profilePicture }
                      : AppImages.Ellipse1
                  }
                  style={{
                    width: 27,
                    height: 27,
                    borderRadius: 100,
                  }}
                />
                <View style={{ flex: 1, flexDirection: "row", columnGap: 5 }}>
                  <Text
                    style={[
                      deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                        ? AppFontStyle.REGULAR_ANYBODY_16
                        : AppFontStyle.REGULAR_ANYBODY_14,
                    ]}
                  >
                    {item.userId}:
                  </Text>
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_12,
                      { opacity: 0.5, lineHeight: 17 },
                    ]}
                  >
                    {item.commentData}
                  </Text>
                </View>
                {item.gift ? (
                  <Image
                    source={item.gift}
                    style={{
                      width: 31,
                      height: 30,
                      borderRadius: 100,
                    }}
                  />
                ) : null}
              </View>
            )}
          />

          {/* Scroll to Bottom Button */}
          {showScrollToBottom && (
            <TouchableOpacity
              onPress={scrollToBottom}
              style={{
                position: "absolute",
                bottom: 60,
                right: 10,
                backgroundColor: "rgba(220, 38, 38, 0.9)",
                borderRadius: 20,
                width: 40,
                height: 40,
                justifyContent: "center",
                alignItems: "center",
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.3,
                shadowRadius: 4,
                elevation: 5,
                zIndex: 1000,
              }}
            >
              <Text
                style={{ color: "white", fontSize: 20, fontWeight: "bold" }}
              >
                ↓
              </Text>
            </TouchableOpacity>
          )}

          {/* Like Animations */}
          {likeAnimations.map((animationId) => (
            <LikeAnimation
              key={animationId}
              onComplete={() => handleAnimationComplete(animationId)}
            />
          ))}
          {deviceSize == DEVICE_SIZES.XS || deviceSize == DEVICE_SIZES.SM ? (
            ""
          ) : (
            <View
              style={{
                height: 1,
                backgroundColor: AppColors.gray_A3A3A3,
                marginVertical: 5,
              }}
            />
          )}
          <View
            style={{
              flexDirection: "row",
              gap: 8,
              alignItems: "center",
              marginVertical: 5,
            }}
          >
            {/* Text Input with Send Button Inside */}
            <View
              style={{
                flex: 1,
                height: 40,
                position: "relative",
              }}
            >
              <TextInput
                ref={inputRef}
                style={{
                  flex: 1,
                  height: 40,
                  paddingVertical: 0,
                  paddingHorizontal: 16,
                  borderRadius: 8,
                  paddingRight: 50,
                  backgroundColor: "#1F2937",
                  color: "#FFFFFF",
                  fontSize: 14,
                  borderWidth: 0.8,
                  borderColor: "#374151",
                }}
                multiline={false}
                placeholderTextColor="#999999"
                placeholder="Write Here"
                value={newMessage}
                onChangeText={setNewMessage}
                onKeyPress={({ nativeEvent }) => {
                  if (nativeEvent.key === "Enter") {
                    handleSendComment();
                  }
                }}
              />

              {/* Send Button - Only show when typing */}
              {newMessage.trim().length > 0 && (
                <View
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    bottom: 0,
                    justifyContent: "center",
                  }}
                >
                  <StreamActionIcon type="send" onPress={handleSendComment} />
                </View>
              )}
            </View>

            {/* Gift Button - Only for Mobile */}
            {isMobile && onOpenMobileGiftSheet && (
              <StreamActionIcon type="gift" onPress={onOpenMobileGiftSheet} />
            )}

            {/* Like Button */}
            <StreamActionIcon type="like" onPress={handleLikeClick} />

            {/* Share Button */}
            <StreamActionIcon
              type="share"
              onPress={() => setShowSharePopup(!showSharePopup)}
            />

            {inviteSocialMediaModal && (
              <InviteSocialMediaModal
                accessToken={""}
                challengeDetails={{}}
                showModal={inviteSocialMediaModal}
                setShowModal={setInviteSocialMediaModal}
                eventType={"LiveStreaming"}
                eventID={""}
                status={""}
              />
            )}

            {isGiftModal && (
              <GiftModal
                styles={styles}
                openGiftModal={isGiftModal}
                closeGiftModal={() => {
                  setIsGiftModal(false);
                }}
                giftData={giftData}
                handleGiftSelect={handleGiftSelect}
                getTokenPopup={getTokenPopup}
                setGetTokenPopup={setGetTokenPopup}
                userData={userData}
              />
            )}
          </View>
        </View>
      </>
    );
  }

  function renderCompactAudiencePoll() {
    const youProgress = 60;
    const nameProgress = 45;
    const staticName = "PlayerXYZ";
    const votes = "1256";
    const remainingTime = "2:15";

    const topGiftersData = [
      { id: "1", name: "User1", gifts: 100 },
      { id: "2", name: "User2", gifts: 75 },
      { id: "3", name: "User3", gifts: 50 },
      { id: "4", name: "User4", gifts: 40 },
    ];

    return (
      <View
        style={{
          flexDirection: "row",
          height: "100%",
          overflow: "hidden",
          padding: 8,
        }}
      >
        {/* Audience Poll Section */}
        <View
          style={{
            flex: 1,
            backgroundColor: AppColors.grayColor_282828,
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
          }}
        >
          <View style={{ padding: 4, flex: 1 }}>
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_8,
                { color: AppColors.purple_C084FC, marginBottom: 2 },
              ]}
            >
              Audience Poll
            </Text>

            <Text style={{ color: "#1E90FF", fontSize: 7, marginBottom: 1 }}>
              You
            </Text>
            <View
              style={{
                width: "100%",
                height: 4,
                backgroundColor: "#2D3748",
                borderRadius: 2,
                marginBottom: 1,
              }}
            >
              <View
                style={{
                  width: `${youProgress}%`,
                  height: "100%",
                  backgroundColor: "#1E90FF",
                  borderRadius: 2,
                }}
              />
            </View>
            <Text
              style={{
                color: "#9CA3AF",
                fontSize: 6,
                textAlign: "right",
                marginBottom: 2,
              }}
            >
              {youProgress}%
            </Text>

            <Text style={{ color: "#FF0000", fontSize: 7, marginBottom: 1 }}>
              {staticName}
            </Text>
            <View
              style={{
                width: "100%",
                height: 4,
                backgroundColor: "#2D3748",
                borderRadius: 2,
                marginBottom: 1,
              }}
            >
              <View
                style={{
                  width: `${nameProgress}%`,
                  height: "100%",
                  backgroundColor: "#FF0000",
                  borderRadius: 2,
                }}
              />
            </View>
            <Text
              style={{
                color: "#9CA3AF",
                fontSize: 6,
                textAlign: "right",
                marginBottom: 2,
              }}
            >
              {nameProgress}%
            </Text>

            <View
              style={{ flexDirection: "row", justifyContent: "center", gap: 2 }}
            >
              <Text style={{ color: AppColors.white, fontSize: 6 }}>
                {votes} votes
              </Text>
              <Text style={{ color: AppColors.white, fontSize: 6 }}>
                {remainingTime} remaining
              </Text>
            </View>
          </View>
        </View>

        {/* Top Gifters Section */}
        <View
          style={{
            flex: 1,
            backgroundColor: AppColors.grayColor_282828,
            borderTopRightRadius: 8,
            borderBottomRightRadius: 8,
          }}
        >
          <View style={{ padding: 4, flex: 1 }}>
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_8,
                { color: AppColors.purple_C084FC, marginBottom: 2 },
              ]}
            >
              Top Gifters
            </Text>

            <View
              style={{
                flex: 1,
                flexDirection: "row",
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              {topGiftersData.slice(0, 4).map((item) => (
                <View
                  key={item.id}
                  style={{
                    width: "48%",
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <View style={{ position: "relative", marginRight: 3 }}>
                    <Image
                      source={AppImages.Ellipse1}
                      style={{ width: 12, height: 12, borderRadius: 6 }}
                    />
                    <View
                      style={{
                        position: "absolute",
                        top: -1,
                        right: -1,
                        backgroundColor: AppColors.purple_6D28D9,
                        borderRadius: 4,
                        width: 8,
                        height: 8,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Text style={{ color: "white", fontSize: 4 }}>
                        {item.gifts}
                      </Text>
                    </View>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{ color: "white", fontSize: 6 }}
                      numberOfLines={1}
                    >
                      {item.name}
                    </Text>
                    <Text style={{ color: "#9CA3AF", fontSize: 5 }}>
                      {item.gifts} gifts
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* Coming Soon Overlay */}
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: 12,
          }}
        >
          <Text
            style={{
              color: "white",
              fontSize: 12,
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            Audience Poll & Top Gifter
          </Text>
          <Text style={comingSoonStyle}>Coming Soon</Text>
        </View>
      </View>
    );
  }
};

const comingSoonStyle = StyleSheet.create({
  text: {
    color: "#FACC15",
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    marginTop: 4,
  },
}).text;

export default StreamChat;
